// GOLDEN PATH CODE
// This code demonstrates how to save and load app state when
// navigating away from the application (for example in a log in flow)]
import { useQuery } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import { geocode } from '../../clients/amazonLocationClient';

export const AUTH_RELOAD_TIMESTAMP_KEY = 'authenticationReloaded';
export const ALIAS_KEY = 'amzn-rv-userId';
export const EMP_KEY = 'amzn-rv-empId';
export const EMP_CITY_KEY = 'amzn-rv-empCity';
export const PERMISSION_KEY = 'amzn-rv-permissions';
export const POST_LOGIN_PATH_KEY = 'POST_LOGIN_PATH';
export const LOGIN_QUERY_KEY = ['Amplify', 'currentSession'];
const USER_REFETCH_MILLS = 30 * 60 * 1000;

export interface UserData {
  userId: string; // this is user alias from idToken
  employeeId: string;
  employeeCity: string;
  employeeOfficeBuilding?: string;
  employeeCountry?: string;
  employeePersonId?: string;
  permissions: string[];
  employeeCityLong?: number;
  employeeCityLat?: number;
}

export interface LoginData extends UserData {
  isPending: boolean;
  isError: boolean;
  error: Error | string | null;
}

function useCachedAuth() {
  return {
    setAuthCache: (userData: UserData) => {
      localStorage.setItem(ALIAS_KEY, userData.userId);
      localStorage.setItem(EMP_KEY, userData.employeeId);
      localStorage.setItem(EMP_CITY_KEY, userData.employeeCity);
    },
    getAuthFromCache: () => {
      return {
        userId: localStorage.getItem(ALIAS_KEY) || '',
        employeeId: localStorage.getItem(EMP_KEY) || '',
        employeeCity: localStorage.getItem(EMP_CITY_KEY) || '',
        // GOLDEN PATH NOTE
        // We do NOT store the permissions to make it harder for
        // bad actors to phish for identities with the permissions
        // that they want.  This is not a worry, because the site is
        // gated on actually loading the permissions, and the data
        // access requires a fresh user token passed via the API
        permissions: [],
      };
    },
  };
}

export function useLoggedInUser(): LoginData {
  const { setAuthCache, getAuthFromCache } = useCachedAuth();

  const userQuery = useQuery({
    queryKey: LOGIN_QUERY_KEY,
    queryFn: async () => {
      try {
        // This must be BEFORE Auth.currentSession or it will automatically logout of cognito and redirect to "/"
        if (!sessionStorage.getItem(POST_LOGIN_PATH_KEY)) {
          sessionStorage.setItem(POST_LOGIN_PATH_KEY, window.location.href);
        }
        const session = await Auth.currentSession();

        const idToken = session.getIdToken();
        const userId = idToken.payload['identities'][0].userId;
        const permissions = idToken.payload['permissions'];

        let lat, long;
        // Skips virtual employees as we don't have granular enough location for them
        try {
          if (
            idToken.payload['custom:city'] != null &&
            idToken.payload['custom:country'] != null &&
            !idToken.payload['custom:city'].toLowerCase().includes('virtual')
          ) {
            // Get the lat/long of the users city for prepopulating search location
            const queryText = [idToken.payload['custom:city'], idToken.payload['custom:country']]
              .filter(Boolean)
              .join(', ');
            const response = await geocode({ QueryText: queryText, IntendedUse: 'Storage' });
            const result = response.ResultItems?.[0];
            lat = result?.Position?.[1];
            long = result?.Position?.[0];
          }
        } catch (e) {
          // If location service fails we default to no lat/long, but it shouldn't prevent login
          console.error('Location service failed to get coordinates for employee', e);
        }

        const userData = {
          userId,
          employeeId: idToken.payload['custom:employee_id'],
          employeeCity: idToken.payload['custom:city'],
          employeeOfficeBuilding: idToken.payload['custom:office'],
          employeeCountry: idToken.payload['custom:country'],
          employeePersonId: idToken.payload['custom:person_id'],
          permissions: permissions ? permissions.split(',') : [],
          employeeCityLat: lat,
          employeeCityLong: long,
        };
        setAuthCache(userData);
        return userData;
      } catch {
        // GOLDEN PATH NOTE
        // Auth.federatedSignIn navigates away from the RedVelvet app entirely
        // We set the redirect in session storage, but only if there is no redirect already set
        // We clear this session storage key after we actually redirect post sign in event in App.tsx
        await Auth.federatedSignIn({
          customProvider: 'AmazonFederate',
        });
      }
    },
    refetchInterval: USER_REFETCH_MILLS,
  });

  return {
    // GOLDEN PATH NOTE
    // By spreading the data or the cached data, we ensure
    // that we can reuse this code in all the components, with
    // a guarantee that we will have an alias string to use in
    // queries
    ...(userQuery.data || getAuthFromCache()),
    ...userQuery,
  };
}
